import { cagesByStatus, OVERVIEW_CAGE_STATUSES, type OverviewLocation } from '@/domain/client';

import type { CageStatus, QuickSearchItem } from './types';

export const QUICK_SEARCH_GROUP_ORDER = [
    'Pages',
    'Clients',
    'Locations',
    'Cages',
    'Bioscopes',
    'Winches',
] as const;

export const CAGE_STATUS_ORDER: readonly CageStatus[] = OVERVIEW_CAGE_STATUSES;

export function parseNavigationId(query: string): number | null {
    const trimmed = query.trim();
    if (!/^\d+$/.test(trimmed)) {
        return null;
    }
    const id = Number(trimmed);
    if (!Number.isSafeInteger(id) || id <= 0) {
        return null;
    }
    return id;
}

export function shouldResolveNavigationId(options: {
    isOpen: boolean;
    isDemoMode: boolean;
    id: number | null;
}): boolean {
    return options.isOpen && !options.isDemoMode && options.id != null;
}

export function parseNavigationSearchTokens(query: string): string[] {
    return query.trim().split(/\s+/).filter(Boolean);
}

export function shouldSearchNavigationNames(options: {
    isOpen: boolean;
    isDemoMode: boolean;
    isOptoScaleAdmin: boolean;
    query: string;
}): boolean {
    if (!options.isOpen || options.isDemoMode || !options.isOptoScaleAdmin) {
        return false;
    }
    if (parseNavigationId(options.query) != null) {
        return false;
    }
    return parseNavigationSearchTokens(options.query).length >= 2;
}

export function locationQuickSearchLabel(
    locationName: string,
    clientName: string,
    isOptoScaleAdmin: boolean
): string {
    return isOptoScaleAdmin ? `${locationName} (${clientName})` : locationName;
}

export function cageQuickSearchLabel(cageName: string, locationName: string): string {
    return `${cageName} (${locationName})`;
}

export function buildLocationQuickSearchItems(options: {
    activeLocations: OverviewLocation[];
    inactiveLocations: OverviewLocation[];
    clientId: number;
    clientName: string;
    isOptoScaleAdmin: boolean;
    canAdminister: (locationId: number) => boolean;
}): QuickSearchItem[] {
    const {
        activeLocations,
        inactiveLocations,
        clientId,
        clientName,
        isOptoScaleAdmin,
        canAdminister,
    } = options;

    const buckets: Array<{ locations: OverviewLocation[]; status: CageStatus }> = [
        { locations: activeLocations, status: 'active' },
        { locations: inactiveLocations, status: 'inactive' },
    ];

    const items: QuickSearchItem[] = [];
    for (const { locations, status } of buckets) {
        for (const location of locations) {
            if (!canAdminister(location.locationId)) {
                continue;
            }
            items.push({
                id: `location-${location.locationId}`,
                kind: 'location',
                label: locationQuickSearchLabel(
                    location.locationName,
                    clientName,
                    isOptoScaleAdmin
                ),
                group: 'Locations',
                url: `/c/${clientId}/location/${location.locationId}`,
                status,
            });
        }
    }
    return items;
}

export function buildCageQuickSearchItems(
    location: OverviewLocation,
    clientId: number
): QuickSearchItem[] {
    const items: QuickSearchItem[] = [];
    for (const { cages, status } of cagesByStatus(location)) {
        for (const cage of cages) {
            items.push({
                id: `cage-${cage.cageId}`,
                kind: 'cage',
                label: cageQuickSearchLabel(cage.cageName, location.locationName),
                group: 'Cages',
                url: `/c/${clientId}/location/${location.locationId}/pen/${cage.cageId}`,
                status,
            });
        }
    }
    return items;
}

export {
    filterAndSortQuickSearchItems,
    matchesQuickSearchQuery,
    mergeQuickSearchItems,
    navigationMatchToQuickSearchItem,
} from './quickSearchFilter';
